<!--
    REV-22479 — the Kontenplan. The ABCP table (`_abcp-table.scss`), and in each of the two value
    columns the Dokumente hub's account cell: AI state, proposal chip (a click changes it), picker.
-->
<div class="coa">
    <!-- The AI is still classifying: one violet line above the table, the rows say which ones. -->
    <div class="ai-working coa__notice" role="status" data-testid="chart-of-accounts-ai-working" *ngIf="working > 0">
        <span class="ai-working__dot" aria-hidden="true"></span>
        <span class="ai-working__text">
            <span class="ai-working__title">{{ 'abcp.chartOfAccounts.aiWorkingNotice' | i18next }}</span>
            <span class="ai-working__step">{{
                'abcp.chartOfAccounts.aiWorkingCount' | i18next : { count: working }
            }}</span>
        </span>
    </div>

    <!-- Accounts without a balance in the periods shown need no position; they are listed on request. -->
    <div class="coa__filters">
        <ham-filter-pill
            data-testid="chart-of-accounts-zero-accounts"
            [label]="'abcp.accountSurface.chip.zeroAccounts' | i18next"
            [active]="withZeroAccounts"
            (pillClick)="zeroAccountsToggle.emit()"
        ></ham-filter-pill>
    </div>

    <div class="coa__frame" data-testid="chart-of-accounts">
        <table class="coa__table">
            <thead>
                <tr>
                    <th class="coa__col-number">{{ 'abcp.chartOfAccounts.columnNumber' | i18next }}</th>
                    <th class="coa__col-name">{{ 'abcp.chartOfAccounts.columnName' | i18next }}</th>
                    <th class="coa__col-value">{{ 'abcp.chartOfAccounts.columnPosition' | i18next }}</th>
                    <th class="coa__col-value">{{ 'abcp.chartOfAccounts.columnFunction' | i18next }}</th>
                </tr>
            </thead>
            <tbody>
                <ng-container *ngIf="loading && rows.length === 0">
                    <tr *ngFor="let _ of skeletonRows">
                        <td colspan="4"><span class="coa__skeleton"></span></td>
                    </tr>
                </ng-container>

                <tr *ngIf="error && !loading">
                    <td colspan="4" class="coa__state coa__state--error" data-testid="chart-of-accounts-error">
                        {{ 'abcp.chartOfAccounts.loadError' | i18next }}
                    </td>
                </tr>

                <tr *ngIf="!loading && !error && rows.length === 0">
                    <td colspan="4" class="coa__state" data-testid="chart-of-accounts-empty">
                        {{ 'abcp.chartOfAccounts.empty' | i18next }}
                    </td>
                </tr>

                <tr
                    class="coa__row"
                    data-testid="chart-of-accounts-row"
                    *ngFor="let row of rows; trackBy: trackByAccountId"
                    [attr.data-account-id]="row.accountId"
                >
                    <td class="coa__number">{{ row.accountNumber }}</td>
                    <td class="coa__name">
                        <span [hamTooltip]="row.name" tooltipClass="abcp" [tooltipOnlyWhenTruncated]="true">{{
                            row.name
                        }}</span>
                    </td>
                    <td>
                        <ng-container *ngTemplateOutlet="cell; context: { row: row, half: 'position' }"></ng-container>
                    </td>
                    <td>
                        <ng-container *ngTemplateOutlet="cell; context: { row: row, half: 'function' }"></ng-container>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</div>

<ng-template #cell let-row="row" let-half="half">
    <ng-container *ngIf="cellOf(row, half) as value">
        <div class="dh-account" [attr.data-testid]="'chart-of-accounts-' + half" [attr.data-state]="value.state">
            <!-- The AI is classifying this half. -->
            <div class="ai-working" role="status" *ngIf="value.state === 'working'">
                <span class="ai-working__dot" aria-hidden="true"></span>
                <span class="ai-working__text">
                    <span class="ai-working__title">{{
                        (half === 'position'
                            ? 'abcp.chartOfAccounts.aiWorkingPosition'
                            : 'abcp.chartOfAccounts.aiWorkingFunction'
                        ) | i18next
                    }}</span>
                </span>
            </div>

            <!-- The half does not apply: no function under this position (or no position yet). -->
            <span class="coa__none" *ngIf="value.state === 'none'">—</span>

            <!-- The picker: no value, or a proposal / decided value opened by a click. -->
            <ng-container *ngIf="value.state !== 'working' && value.state !== 'none' && showsPicker(row, half)">
                <!-- The Dokumente hub's picker: amber trigger, searchable panel in the flyout layer. -->
                <div class="dh-picker coa__picker">
                    <button
                        class="dh-picker__trigger"
                        type="button"
                        [attr.data-testid]="'chart-of-accounts-picker-' + half"
                        [attr.aria-expanded]="isPickerOpen(row, half)"
                        [disabled]="isPending(row, half)"
                        (click)="onPickerToggle(row, half, $any($event.currentTarget)); $event.stopPropagation()"
                    >
                        {{
                            value.label ??
                                ((half === 'position'
                                    ? 'abcp.chartOfAccounts.pickPosition'
                                    : 'abcp.chartOfAccounts.pickFunction'
                                ) | i18next)
                        }}
                        <i class="dh-picker__chevron" aria-hidden="true"></i>
                    </button>

                    <div
                        #pickerPanel
                        class="dh-picker__panel"
                        [attr.data-testid]="'chart-of-accounts-picker-panel-' + half"
                        hamFlyout
                        [position]="pickerPosition"
                        *ngIf="isPickerOpen(row, half)"
                        (closeFlyout)="closePicker()"
                        (click)="$event.stopPropagation()"
                    >
                        <div class="dh-picker__search">
                            <lucide-icon name="search" [size]="13" aria-hidden="true"></lucide-icon>
                            <input
                                type="text"
                                data-testid="chart-of-accounts-picker-search"
                                [value]="pickerSearch"
                                [attr.placeholder]="'abcp.chartOfAccounts.search' | i18next"
                                [attr.aria-label]="'abcp.chartOfAccounts.search' | i18next"
                                (input)="pickerSearch = $any($event.target).value"
                            />
                        </div>
                        <div class="dh-picker__list">
                            <button
                                class="dh-picker__row"
                                type="button"
                                data-testid="chart-of-accounts-picker-option"
                                *ngFor="let option of filteredOptions(row, half)"
                                [attr.data-key]="option.key"
                                (click)="onPick(row, half, option.key)"
                            >
                                <span class="dh-picker__name">{{ option.label }}</span>
                                <lucide-icon
                                    *ngIf="option.key === value.value"
                                    name="check"
                                    [size]="13"
                                    aria-hidden="true"
                                ></lucide-icon>
                            </button>
                            <div class="dh-picker__empty" *ngIf="!filteredOptions(row, half).length">
                                {{ 'abcp_documents_picker_empty' | i18next }}
                            </div>
                        </div>
                    </div>
                </div>
                <button
                    class="dh-icon-btn"
                    type="button"
                    data-testid="chart-of-accounts-cancel"
                    *ngIf="value.value !== null"
                    [attr.aria-label]="'abcp.chartOfAccounts.cancel' | i18next"
                    [hamTooltip]="'abcp.chartOfAccounts.cancel' | i18next"
                    tooltipClass="abcp"
                    (click)="onCancelPicker(row, half)"
                >
                    <lucide-icon name="undo-2" [size]="12" aria-hidden="true"></lucide-icon>
                </button>
            </ng-container>

            <!-- A proposal counts as it is: the blue chip is the only control, a click opens the picker. -->
            <button
                class="dh-chip dh-chip--proposal"
                type="button"
                [attr.data-testid]="'chart-of-accounts-proposal-' + half"
                *ngIf="value.state === 'proposal' && !showsPicker(row, half)"
                [attr.aria-label]="('abcp.chartOfAccounts.change' | i18next) + ': ' + value.label"
                [hamTooltip]="'abcp.chartOfAccounts.change' | i18next"
                tooltipClass="abcp"
                [disabled]="isPending(row, half)"
                (click)="onOpenPicker(row, half)"
            >
                <lucide-icon name="sparkles" [size]="11" aria-hidden="true"></lucide-icon>
                {{ value.label }}
            </button>

            <!-- Decided by a person: the green chip; a click opens the picker to change it. -->
            <button
                class="dh-chip dh-chip--account coa__decided"
                type="button"
                data-origin="manual"
                [attr.data-testid]="'chart-of-accounts-decided-' + half"
                *ngIf="value.state === 'decided' && !showsPicker(row, half)"
                [attr.aria-label]="('abcp.chartOfAccounts.change' | i18next) + ': ' + value.label"
                [hamTooltip]="'abcp.chartOfAccounts.change' | i18next"
                tooltipClass="abcp"
                [disabled]="isPending(row, half)"
                (click)="onOpenPicker(row, half)"
            >
                <lucide-icon name="check" [size]="11" aria-hidden="true"></lucide-icon>
                {{ value.label }}
            </button>

            <span
                class="dh-proposal-error"
                data-testid="chart-of-accounts-save-error"
                *ngIf="hasFailed(row, half)"
                [hamTooltip]="'abcp.chartOfAccounts.saveError' | i18next"
                tooltipClass="abcp"
            >
                <lucide-icon name="alert-circle" [size]="12" aria-hidden="true"></lucide-icon>
            </span>
        </div>
    </ng-container>
</ng-template>
